import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { track } from '../../utils/tracking';
import { generateBusinessFundingPageSchema } from '../../utils/schema';

export default function BusinessFundingEducation() {
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    track('page_view', { page: 'business_funding_education' });
  }, []);

  useEffect(() => {
    document.title = 'What is Business Funding? Complete Guide to Business Loans & Financing';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Learn everything about business funding: what lenders look for, types of financing, qualification requirements, and how to prepare your business for funding success. Educational guide for business owners.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'business funding, business loans, what lenders look for, business financing, loan requirements, business credit, revenue requirements, collateral');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${window.location.origin}/business-funding`);

    // Add page schema with breadcrumbs
    const existingSchema = document.querySelector('script[data-schema="business-funding"]');
    if (existingSchema) {
      existingSchema.remove();
    }
    const schemaScript = document.createElement('script');
    schemaScript.setAttribute('type', 'application/ld+json');
    schemaScript.setAttribute('data-schema', 'business-funding');
    schemaScript.textContent = JSON.stringify(generateBusinessFundingPageSchema());
    document.head.appendChild(schemaScript);

    return () => {
      const schema = document.querySelector('script[data-schema="business-funding"]');
      if (schema) schema.remove();
    };
  }, []);

  const lenderCriteria = [
    {
      title: 'Credit Profile',
      icon: 'ri-bank-card-2-line',
      importance: 'Critical',
      color: 'from-rose-500 to-red-600',
      description: 'Your credit history tells lenders how you\'ve managed debt in the past. Both personal and business credit scores matter.',
      details: [
        { label: 'Personal Credit Score', info: 'Most lenders require 600+ minimum, with 680+ preferred for best rates. Scores above 720 unlock premium terms.' },
        { label: 'Business Credit Score', info: 'Dun & Bradstreet PAYDEX, Experian Business, and Equifax Business scores. Aim for 80+ PAYDEX.' },
        { label: 'Credit History Length', info: 'Longer credit history demonstrates stability. Lenders prefer 3+ years of credit history.' },
        { label: 'Payment History', info: 'Late payments, collections, and defaults significantly impact approval odds and rates.' },
        { label: 'Credit Utilization', info: 'Keep utilization below 30% on revolving accounts. High utilization signals financial stress.' }
      ],
      tips: [
        'Check your credit reports for errors before applying',
        'Pay down credit card balances to lower utilization',
        'Don\'t open new credit accounts before applying',
        'Build business credit by getting vendor accounts that report'
      ]
    },
    {
      title: 'Revenue & Cash Flow',
      icon: 'ri-money-dollar-circle-line',
      importance: 'Critical',
      color: 'from-emerald-500 to-teal-600',
      description: 'Lenders need to see that your business generates enough money to repay the loan while maintaining operations.',
      details: [
        { label: 'Monthly Revenue', info: 'Most lenders require $10,000-$30,000+ monthly revenue. Higher revenue = more funding options.' },
        { label: 'Revenue Consistency', info: 'Stable, predictable revenue is preferred over volatile income. Seasonal businesses may need extra documentation.' },
        { label: 'Cash Flow Analysis', info: 'Lenders examine bank statements for average daily balance, deposit frequency, and overdrafts.' },
        { label: 'Debt Service Coverage Ratio (DSCR)', info: 'Your net operating income divided by debt payments. Lenders typically want 1.25x or higher.' },
        { label: 'Profit Margins', info: 'Healthy margins indicate a sustainable business that can handle debt payments.' }
      ],
      tips: [
        'Maintain consistent deposits in your business account',
        'Avoid overdrafts and NSF fees in the months before applying',
        'Keep personal and business finances completely separate',
        'Document any large or unusual deposits with explanations'
      ]
    },
    {
      title: 'Time in Business',
      icon: 'ri-calendar-check-line',
      importance: 'High',
      color: 'from-amber-500 to-orange-600',
      description: 'The longer your business has been operating, the more data lenders have to assess risk and the more options available.',
      details: [
        { label: '0-6 Months', info: 'Very limited options. May qualify for microloans, business credit cards, or revenue-based financing with strong revenue.' },
        { label: '6-12 Months', info: 'More options open up including merchant cash advances and some short-term loans. Still considered high-risk.' },
        { label: '1-2 Years', info: 'Significantly more options. Can qualify for most alternative lenders and some traditional products.' },
        { label: '2+ Years', info: 'Full range of options including SBA loans, traditional bank loans, and best rates from alternative lenders.' },
        { label: '5+ Years', info: 'Premium terms available. Established track record opens doors to the most competitive financing.' }
      ],
      tips: [
        'If you\'re new, focus on building revenue and credit first',
        'Document your industry experience even if the business is new',
        'Consider starting with smaller funding to build a repayment track record',
        'Keep all business records organized from day one'
      ]
    },
    {
      title: 'Collateral & Assets',
      icon: 'ri-shield-check-line',
      importance: 'Medium-High',
      color: 'from-indigo-500 to-purple-600',
      description: 'Collateral reduces lender risk by providing assets they can claim if you default. Not all loans require collateral.',
      details: [
        { label: 'Real Estate', info: 'Commercial or residential property. Typically can borrow 70-80% of appraised value.' },
        { label: 'Equipment', info: 'Machinery, vehicles, technology. Equipment financing uses the equipment itself as collateral.' },
        { label: 'Inventory', info: 'Raw materials or finished goods. Usually valued at 50-80% of liquidation value.' },
        { label: 'Accounts Receivable', info: 'Outstanding invoices from creditworthy customers. Can be used for invoice factoring or as collateral.' },
        { label: 'Personal Guarantee', info: 'Most small business loans require owners to personally guarantee the debt, putting personal assets at risk.' }
      ],
      tips: [
        'Know the value of your business assets before applying',
        'Understand what a personal guarantee means for your personal finances',
        'Unsecured loans exist but typically have higher rates',
        'Equipment financing is often easier to qualify for since the equipment secures the loan'
      ]
    },
    {
      title: 'Industry & Business Type',
      icon: 'ri-building-2-line',
      importance: 'Medium',
      color: 'from-cyan-500 to-blue-600',
      description: 'Some industries are considered higher risk than others, affecting approval odds and terms.',
      details: [
        { label: 'Low-Risk Industries', info: 'Healthcare, professional services, manufacturing, wholesale. Generally easier to fund with better terms.' },
        { label: 'Medium-Risk Industries', info: 'Retail, restaurants, construction, transportation. More scrutiny but still fundable.' },
        { label: 'High-Risk Industries', info: 'Cannabis, gambling, adult entertainment, firearms. Limited lender options and higher rates.' },
        { label: 'Restricted Industries', info: 'Some lenders won\'t fund certain industries at all regardless of financials.' },
        { label: 'Business Structure', info: 'LLCs and corporations are preferred over sole proprietorships for liability protection.' }
      ],
      tips: [
        'Research which lenders specialize in your industry',
        'Be prepared to explain your business model clearly',
        'Highlight industry experience and competitive advantages',
        'If in a high-risk industry, expect to provide more documentation'
      ]
    },
    {
      title: 'Use of Funds',
      icon: 'ri-file-list-3-line',
      importance: 'Medium',
      color: 'from-pink-500 to-rose-600',
      description: 'Lenders want to know how you\'ll use the money and whether it will help your business grow and repay the loan.',
      details: [
        { label: 'Working Capital', info: 'Day-to-day operations, payroll, inventory. Common and generally approved.' },
        { label: 'Equipment Purchase', info: 'Specific assets that add value. Easy to justify and often has dedicated loan products.' },
        { label: 'Expansion', info: 'New locations, hiring, marketing. Shows growth ambition but needs solid projections.' },
        { label: 'Debt Refinancing', info: 'Consolidating existing debt. Can be approved if it improves cash flow.' },
        { label: 'Real Estate', info: 'Property purchase or improvement. Requires specific loan products and more documentation.' }
      ],
      tips: [
        'Be specific about how funds will be used',
        'Show how the funding will generate ROI',
        'Avoid vague answers like "general business purposes"',
        'Match the loan type to your specific need'
      ]
    }
  ];

  const fundingTypes = [
    {
      name: 'Working Capital Loans',
      description: 'Short-term financing for day-to-day operations, inventory, payroll, or bridging cash flow gaps.',
      amount: '$10,000 - $500,000',
      term: '3 - 18 months',
      speed: '24-72 hours',
      requirements: '6+ months in business, $10K+ monthly revenue, 550+ credit',
      bestFor: 'Seasonal businesses, covering operational expenses, emergency cash needs',
      icon: 'ri-funds-line',
      link: '/funding/working-capital'
    },
    {
      name: 'Business Line of Credit',
      description: 'Revolving credit you can draw from as needed, only paying interest on what you use.',
      amount: '$10,000 - $250,000',
      term: 'Revolving (renews annually)',
      speed: '3-7 days',
      requirements: '1+ year in business, $15K+ monthly revenue, 600+ credit',
      bestFor: 'Ongoing cash flow management, unexpected expenses, flexible access to capital',
      icon: 'ri-exchange-funds-line',
      link: '/funding/line-of-credit'
    },
    {
      name: 'Term Loans',
      description: 'Traditional loans with fixed amounts, rates, and repayment schedules for major investments.',
      amount: '$50,000 - $5,000,000',
      term: '1 - 10 years',
      speed: '1-4 weeks',
      requirements: '2+ years in business, $25K+ monthly revenue, 650+ credit',
      bestFor: 'Major purchases, expansion projects, long-term investments',
      icon: 'ri-bank-line',
      link: '/funding/term-loans'
    },
    {
      name: 'Equipment Financing',
      description: 'Loans specifically for purchasing business equipment, with the equipment serving as collateral.',
      amount: '$25,000 - $1,000,000+',
      term: '1 - 7 years',
      speed: '3-10 days',
      requirements: '1+ year in business, equipment quote, 600+ credit',
      bestFor: 'Machinery, vehicles, technology, medical equipment, manufacturing equipment',
      icon: 'ri-tools-line',
      link: '/funding/equipment-financing'
    },
    {
      name: 'Commercial Real Estate Loans',
      description: 'Long-term financing for purchasing, refinancing, or renovating commercial property.',
      amount: '$100,000 - $10,000,000+',
      term: '5 - 25 years',
      speed: '30-90 days',
      requirements: '2+ years in business, 20-30% down payment, 680+ credit, property appraisal',
      bestFor: 'Office buildings, retail spaces, warehouses, multi-family properties',
      icon: 'ri-building-4-line',
      link: '/funding/commercial-real-estate'
    }
  ];

  const applicationProcess = [
    {
      step: 1,
      title: 'Assess Your Needs',
      description: 'Determine exactly how much funding you need and what you\'ll use it for. Over-borrowing costs money; under-borrowing may leave you short.',
      icon: 'ri-search-line',
      duration: '1-2 days',
      tasks: ['Calculate exact funding amount needed', 'Define specific use of funds', 'Determine ideal repayment timeline', 'Research which loan type fits best']
    },
    {
      step: 2,
      title: 'Check Your Qualifications',
      description: 'Review your credit scores, financial statements, and business metrics to understand where you stand before applying.',
      icon: 'ri-checkbox-circle-line',
      duration: '1-3 days',
      tasks: ['Pull personal and business credit reports', 'Review last 3-6 months of bank statements', 'Calculate monthly revenue and cash flow', 'Identify any red flags to address']
    },
    {
      step: 3,
      title: 'Gather Documentation',
      description: 'Prepare all required documents in advance to speed up the application process and demonstrate organization.',
      icon: 'ri-folder-line',
      duration: '2-5 days',
      tasks: ['Business and personal tax returns (2 years)', 'Bank statements (3-6 months)', 'Business licenses and registrations', 'Financial statements if available', 'Business plan for larger loans']
    },
    {
      step: 4,
      title: 'Compare Options',
      description: 'Don\'t just apply to one lender. Compare rates, terms, fees, and requirements from multiple sources.',
      icon: 'ri-scales-3-line',
      duration: '1-3 days',
      tasks: ['Research multiple lenders', 'Compare APR (not just interest rate)', 'Understand all fees involved', 'Read reviews and check reputation', 'Consider speed vs. cost tradeoffs']
    },
    {
      step: 5,
      title: 'Submit Application',
      description: 'Complete the application accurately and thoroughly. Incomplete applications cause delays.',
      icon: 'ri-send-plane-line',
      duration: '1-2 hours',
      tasks: ['Fill out application completely', 'Double-check all information', 'Upload all required documents', 'Respond promptly to any questions']
    },
    {
      step: 6,
      title: 'Review & Accept',
      description: 'Carefully review the offer terms before accepting. Understand exactly what you\'re agreeing to.',
      icon: 'ri-file-check-line',
      duration: '1-3 days',
      tasks: ['Review all terms and conditions', 'Calculate total cost of borrowing', 'Understand prepayment penalties', 'Negotiate if possible', 'Sign and receive funds']
    }
  ];

  const commonMistakes = [
    {
      mistake: 'Not Knowing Your Credit Score',
      consequence: 'Applying for products you don\'t qualify for wastes time and can result in multiple hard inquiries.',
      solution: 'Check both personal and business credit scores before applying. Many services offer free credit monitoring.'
    },
    {
      mistake: 'Mixing Personal and Business Finances',
      consequence: 'Makes it difficult for lenders to assess your business\'s true financial health.',
      solution: 'Open a dedicated business bank account and use it exclusively for business transactions.'
    },
    {
      mistake: 'Applying for Too Much or Too Little',
      consequence: 'Over-borrowing increases costs; under-borrowing may leave you unable to complete your project.',
      solution: 'Calculate your exact needs plus a 10-15% buffer for unexpected expenses.'
    },
    {
      mistake: 'Ignoring the Total Cost of Borrowing',
      consequence: 'A low interest rate can be misleading if there are high origination fees or other charges.',
      solution: 'Always calculate the APR and total repayment amount, not just the stated interest rate.'
    },
    {
      mistake: 'Waiting Until You\'re Desperate',
      consequence: 'Urgency limits your options and negotiating power. You may accept unfavorable terms.',
      solution: 'Plan ahead and apply for funding before you urgently need it. Build relationships with lenders early.'
    },
    {
      mistake: 'Not Reading the Fine Print',
      consequence: 'Hidden fees, prepayment penalties, or unfavorable terms can cost you significantly.',
      solution: 'Read every document carefully. Ask questions about anything you don\'t understand.'
    }
  ];

  const faqs = [
    {
      question: 'What\'s the difference between a loan and a line of credit?',
      answer: 'A loan gives you a lump sum upfront that you repay over a fixed term with interest. A line of credit is revolving—you can draw funds as needed up to your limit, repay, and draw again. You only pay interest on what you actually use. Loans are better for one-time purchases; lines of credit are better for ongoing or unpredictable needs.'
    },
    {
      question: 'Will applying for business funding hurt my credit score?',
      answer: 'It depends. Pre-qualification or soft credit checks don\'t affect your score. However, formal applications typically involve a hard credit inquiry, which can temporarily lower your score by a few points. Multiple hard inquiries in a short period can have a larger impact. That\'s why it\'s important to research and pre-qualify before formally applying.'
    },
    {
      question: 'How long does it take to get approved for business funding?',
      answer: 'Timeline varies significantly by funding type. Online lenders and alternative financing can approve in 24-72 hours. Traditional bank loans typically take 2-4 weeks. SBA loans can take 60-90 days. Equipment financing usually takes 3-10 days. Commercial real estate loans often take 30-90 days due to appraisals and documentation requirements.'
    },
    {
      question: 'What\'s the minimum credit score needed for business funding?',
      answer: 'Minimums vary by lender and product. Some alternative lenders work with scores as low as 500-550 for revenue-based financing. Most traditional products require 600-650 minimum. For the best rates and terms, you\'ll want 680+. SBA loans typically require 650-680+. The higher your score, the more options and better terms you\'ll have.'
    },
    {
      question: 'Can I get business funding with no collateral?',
      answer: 'Yes, unsecured business loans exist, but they typically have higher interest rates to compensate for the increased lender risk. Working capital loans, lines of credit, and merchant cash advances are often unsecured. However, most will still require a personal guarantee, meaning you\'re personally liable if the business can\'t repay.'
    },
    {
      question: 'What documents do I need to apply for business funding?',
      answer: 'Common requirements include: business and personal tax returns (1-2 years), bank statements (3-6 months), business licenses, proof of ownership, financial statements (P&L, balance sheet), and a business plan for larger loans. Some online lenders require minimal documentation—just bank statements and basic business info. SBA and traditional bank loans require extensive documentation.'
    },
    {
      question: 'What is a personal guarantee and should I be concerned?',
      answer: 'A personal guarantee means you\'re personally responsible for repaying the loan if your business can\'t. This puts your personal assets (home, savings, etc.) at risk. Most small business loans require one. You should understand this risk before signing. Some lenders offer limited personal guarantees or may waive them for very strong businesses.'
    },
    {
      question: 'How do lenders calculate how much I can borrow?',
      answer: 'Lenders typically look at your monthly revenue, existing debt payments, and cash flow. A common formula is the Debt Service Coverage Ratio (DSCR)—your net operating income divided by your debt payments. Most lenders want to see 1.25x or higher, meaning you earn 25% more than your debt obligations. They may also consider your industry, time in business, and collateral.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200 py-3 px-4" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-amber-600 transition-colors">
                Home
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <span className="text-gray-900 font-medium">Business Funding Guide</span>
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/9d3d8f99744f9dfaba83cefccf53312d.jpg"
            alt="Business funding consultation with financial advisor explaining loan options and requirements"
            title="Complete Guide to Business Funding - Educational Resource"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full text-sm font-medium text-white mb-6">
              <i className="ri-book-open-line text-amber-400"></i>
              <span>Educational Guide</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Understanding
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
                Business Funding
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              Learn what business funding is, how lenders evaluate applications, and what you need to know before applying. This comprehensive guide helps you understand the funding landscape so you can make informed decisions.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#what-lenders-look-for"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Learn What Lenders Evaluate
                <i className="ri-arrow-down-line ml-2"></i>
              </a>
              <a
                href="#funding-types"
                className="inline-flex items-center justify-center px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                Compare Funding Types
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Business Funding */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4">
                The Basics
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Business Funding?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Business funding refers to the capital that businesses obtain to start, operate, or expand their operations. Unlike personal loans, business funding is specifically designed for commercial purposes and is evaluated based on your business's financial health, not just your personal finances.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Funding can come from various sources: traditional banks, credit unions, online lenders, SBA-backed loans, investors, or alternative financing companies. Each source has different requirements, rates, terms, and approval processes.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className="ri-lightbulb-line text-amber-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Key Insight</h3>
                    <p className="text-sm text-gray-700">
                      The "best" funding option depends entirely on your specific situation—your business's age, revenue, credit profile, and what you need the money for. There's no one-size-fits-all solution.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Business%20owner%20reviewing%20financial%20documents%20and%20charts%20on%20desk%20with%20laptop%20calculator%20and%20coffee%20modern%20office%20setting%20warm%20natural%20lighting%20professional%20atmosphere%20organized%20workspace&width=600&height=500&seq=bizfund002&orientation=landscape"
                alt="Business owner reviewing financial documents and funding options on laptop"
                title="Understanding Business Funding Requirements"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg">
                    <i className="ri-funds-line text-emerald-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">$50K+</div>
                    <div className="text-sm text-gray-500">Typical minimum</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Lenders Look For - Main Section */}
      <section id="what-lenders-look-for" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 text-sm font-semibold rounded-full mb-4">
              Critical Knowledge
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Lenders Look For
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding how lenders evaluate applications is crucial for success. Here are the six key factors that determine your eligibility and terms.
            </p>
          </div>

          {/* Interactive Criteria Selector */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left - Criteria List */}
            <div className="lg:col-span-1 space-y-3">
              {lenderCriteria.map((criteria, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSection(index)}
                  className={`w-full text-left p-4 rounded-xl transition-all cursor-pointer ${
                    activeSection === index
                      ? 'bg-white shadow-lg border-2 border-amber-400'
                      : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br ${criteria.color}`}>
                      <i className={`${criteria.icon} text-white text-lg`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{criteria.title}</h3>
                      <span className={`text-xs font-medium ${
                        criteria.importance === 'Critical' ? 'text-rose-600' :
                        criteria.importance === 'High' ? 'text-amber-600' : 'text-gray-500'
                      }`}>
                        {criteria.importance} Factor
                      </span>
                    </div>
                    <i className={`ri-arrow-right-s-line text-gray-400 ${activeSection === index ? 'text-amber-500' : ''}`}></i>
                  </div>
                </button>
              ))}
            </div>

            {/* Right - Detailed Content */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className={`bg-gradient-to-r ${lenderCriteria[activeSection].color} p-6`}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl">
                      <i className={`${lenderCriteria[activeSection].icon} text-white text-2xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">{lenderCriteria[activeSection].title}</h3>
                      <span className="text-white/80 text-sm">{lenderCriteria[activeSection].importance} Factor in Lending Decisions</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {lenderCriteria[activeSection].description}
                  </p>

                  {/* Details */}
                  <h4 className="font-semibold text-gray-900 mb-4">What Lenders Evaluate:</h4>
                  <div className="space-y-4 mb-8">
                    {lenderCriteria[activeSection].details.map((detail, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <h5 className="font-semibold text-gray-900 mb-1">{detail.label}</h5>
                        <p className="text-sm text-gray-600">{detail.info}</p>
                      </div>
                    ))}
                  </div>

                  {/* Tips */}
                  <h4 className="font-semibold text-gray-900 mb-4">Pro Tips:</h4>
                  <ul className="space-y-3">
                    {lenderCriteria[activeSection].tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-emerald-600 text-sm"></i>
                        </div>
                        <span className="text-gray-700">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Funding */}
      <section id="funding-types" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
              Your Options
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Types of Business Funding
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Different funding types serve different purposes. Understanding each option helps you choose the right fit for your needs.
            </p>
          </div>

          <div className="space-y-6">
            {fundingTypes.map((type, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all">
                <div className="grid md:grid-cols-4 gap-6 p-6">
                  {/* Main Info */}
                  <div className="md:col-span-2">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex-shrink-0">
                        <i className={`${type.icon} text-amber-600 text-xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{type.name}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{type.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Amount Range</span>
                      <p className="font-semibold text-gray-900">{type.amount}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Term Length</span>
                      <p className="font-semibold text-gray-900">{type.term}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Funding Speed</span>
                      <p className="font-semibold text-emerald-600">{type.speed}</p>
                    </div>
                  </div>

                  {/* Requirements & CTA */}
                  <div className="space-y-4">
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Typical Requirements</span>
                      <p className="text-sm text-gray-700">{type.requirements}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Best For</span>
                      <p className="text-sm text-gray-700">{type.bestFor}</p>
                    </div>
                    <Link
                      to={type.link}
                      className="inline-flex items-center gap-2 text-amber-600 font-semibold text-sm hover:text-amber-700 transition-colors cursor-pointer"
                    >
                      Learn More
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-amber-500/20 text-amber-400 text-sm font-semibold rounded-full mb-4">
              Step by Step
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Funding Application Process
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              A well-prepared application significantly increases your chances of approval. Follow these steps for the best results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {applicationProcess.map((step, index) => (
              <div key={index} className="bg-gray-800/50 border border-gray-700 rounded-2xl p-6 hover:bg-gray-800/70 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg text-white font-bold">
                    {step.step}
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-700 rounded-lg">
                    <i className={`${step.icon} text-amber-400 text-lg`}></i>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{step.description}</p>
                <div className="text-xs text-amber-400 font-medium mb-4">
                  <i className="ri-time-line mr-1"></i>
                  Typical Duration: {step.duration}
                </div>
                <ul className="space-y-2">
                  {step.tasks.map((task, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                      <i className="ri-checkbox-blank-circle-line text-gray-600 mt-1 text-xs"></i>
                      {task}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 text-sm font-semibold rounded-full mb-4">
              Avoid These Pitfalls
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common Funding Mistakes
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Learn from others' mistakes. These are the most common errors business owners make when seeking funding.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonMistakes.map((item, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-rose-100 rounded-xl mb-4">
                  <i className="ri-error-warning-line text-rose-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.mistake}</h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-xs text-rose-600 font-semibold uppercase tracking-wider">The Problem</span>
                    <p className="text-sm text-gray-600 mt-1">{item.consequence}</p>
                  </div>
                  <div>
                    <span className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">The Solution</span>
                    <p className="text-sm text-gray-700 mt-1">{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-4">
              Got Questions?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Answers to the most common questions about business funding
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden group"
              >
                <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <i className="ri-add-line text-2xl text-amber-500 group-open:rotate-45 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Take the Next Step?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Now that you understand what lenders look for, see which funding options might be available for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/prequalification"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              Check Pre-Qualification
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/cities"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-map-pin-line mr-2"></i>
              Find Your City
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 bg-gray-100 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3 text-sm text-gray-600">
            <i className="ri-information-line text-gray-400 mt-0.5"></i>
            <p>
              <strong>Educational Disclaimer:</strong> This content is for educational purposes only and does not constitute financial advice. We are not a lender. Approval, rates, and terms vary by lender and individual circumstances. Always consult with a qualified financial professional before making funding decisions. We may receive compensation if funding is secured through our partner network.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
